import { useI18n } from '../i18n';
import { interpolate } from './format';

/**
 * Panneau de facettes générique (onglets Dossiers / Tags). Liste de valeurs
 * sélectionnables (préfixe de dossier ou tag) avec leur compteur ; la sélection
 * filtre la grille. États loading / erreur / vide gérés proprement.
 */
export interface FacetItem {
  /** Valeur envoyée à l'API (préfixe de dossier ou id/label de tag). */
  value: string;
  /** Libellé affiché (dossier lisible ou label de tag). */
  label: string;
  count: number;
}

interface FacetPanelProps {
  title: string;
  items: FacetItem[];
  loading: boolean;
  errorCode: string | null;
  emptyLabel: string;
  emptyHint?: string | undefined;
  /** Gabarit i18n de l'aria-label de sélection (`{path}`/`{label}`). */
  selectTemplate: string;
  selectVar: 'path' | 'label';
  /**
   * Libellé de compte ACCORDÉ EN NOMBRE : le pluriel dépend de la langue ET de la
   * valeur, un simple gabarit à interpoler ne suffit donc pas.
   */
  countLabel: (count: number) => string;
  selected?: string | undefined;
  onSelect: (value: string | undefined) => void;
}

export function FacetPanel({
  title,
  items,
  loading,
  errorCode,
  emptyLabel,
  emptyHint,
  selectTemplate,
  selectVar,
  countLabel,
  selected,
  onSelect,
}: FacetPanelProps): React.JSX.Element {
  const { t } = useI18n();

  return (
    <section aria-label={title} className="w-full">
      <h2 className="mb-2 text-sm font-semibold text-fg">{title}</h2>

      {loading && (
        <ul className="space-y-1" aria-hidden="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <li
              key={i}
              className="h-8 animate-pulse rounded-md bg-surface-2 motion-reduce:animate-none"
            />
          ))}
        </ul>
      )}

      {!loading && errorCode && (
        <p role="alert" className="text-sm text-danger">
          {t('gallery.errorTitle')}
        </p>
      )}

      {!loading && !errorCode && items.length === 0 && (
        <div className="text-sm text-muted">
          <p>{emptyLabel}</p>
          {emptyHint && <p className="mt-1">{emptyHint}</p>}
        </div>
      )}

      {!loading && !errorCode && items.length > 0 && (
        <ul className="space-y-1">
          {items.map((item) => {
            const active = selected === item.value;
            return (
              <li key={item.value}>
                <button
                  type="button"
                  aria-pressed={active}
                  aria-label={interpolate(selectTemplate, { [selectVar]: item.label })}
                  onClick={() => onSelect(active ? undefined : item.value)}
                  className={`flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-start text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none ${
                    active ? 'bg-surface-2 font-semibold text-brand' : 'text-fg hover:bg-surface-2'
                  }`}
                >
                  <span className="truncate">{item.label}</span>
                  <span className="shrink-0 text-xs text-muted">{countLabel(item.count)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
